import { createAsyncThunk } from "@reduxjs/toolkit";
import API from "../../../Api/Api";
import endpoints from "../../../Api/Endpoints";

const firstArray = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) return value;
  }
  return [];
};

const pick = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const normalizePlaceholderText = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const lowered = text.toLowerCase();
  if (lowered === "unknown" || lowered === "unknown size" || lowered === "n/a" || lowered === "na") {
    return "";
  }
  return text;
};

const formatUploadedAt = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  const h24 = date.getHours();
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  const hh = String(h12).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${yy}, ${hh}:${mm} ${ampm}`;
};

const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!size || Number.isNaN(size)) return "";

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getPatientName = (record) =>
  pick(
    record?.patientName,
    record?.patient?.name,
    record?.patient?.fullName,
    record?.appUser?.name,
    record?.appUser?.fullName,
    record?.user?.name,
    record?.owner?.name,
    "Patient"
  );

const getPatientLocation = (record) =>
  normalizePlaceholderText(
    pick(
    record?.location,
    record?.patient?.location,
    record?.patient?.city,
    record?.patient?.address?.city,
    record?.user?.location
    )
  );

const getPatientAvatar = (record) =>
  pick(
    record?.patientAvatar,
    record?.patientImage,
    record?.avatar,
    record?.image,
    record?.imageUrl,
    record?.patient?.avatar,
    record?.patient?.image,
    record?.patient?.imageUrl,
    record?.patient?.profileImage,
    record?.patient?.profileImageUrl,
    record?.appUser?.avatar,
    record?.appUser?.image,
    record?.appUser?.imageUrl,
    record?.appUser?.profileImage,
    record?.appUser?.profileImageUrl,
    record?.user?.avatar,
    record?.user?.image
  );

const getDocsFromRecord = (record) =>
  firstArray(
    record?.documents,
    record?.items,
    record?.files,
    record?.prescriptions,
    record?.reports,
    record?.prescriptionFiles,
    record?.reportFiles,
    record?.medicalReports,
    record?.medicalReportFiles,
    record?.attachments,
    record?.uploads
  );

const normalizeDocType = (doc, fallbackType = "prescription") => {
  const raw = String(
    pick(
      doc?.type,
      doc?.documentType,
      doc?.fileType,
      doc?.category,
      doc?.docType,
      fallbackType
    )
  )
    .trim()
    .toLowerCase()
    .replace(/[\s-]/g, "_");

  if (raw.includes("insurance")) {
    return "insurance";
  }

  if (
    raw.includes("report") ||
    raw.includes("medical_report") ||
    raw.includes("medicalreport")
  ) {
    return "report";
  }

  if (
    raw.includes("prescription") ||
    raw.includes("rx") ||
    raw.includes("doctor_note")
  ) {
    return "prescription";
  }

  return fallbackType;
};

const normalizeDocument = (record, doc, index) => {
  const guessedType =
    record?.medicalReportFiles ||
    record?.medicalReports ||
    record?.reports ||
    doc?.reportFiles
      ? "report"
      : "prescription";

  const type = normalizeDocType(doc, guessedType);
  const sizeText = normalizePlaceholderText(pick(doc?.fileSize, formatFileSize(doc?.size), ""));

  return {
    id: pick(doc?._id, doc?.id, `${pick(record?._id, record?.id, "doc")}-${index}`),
    prescriptionId: pick(record?._id, record?.id, ""),
    matchRequestId: pick(doc?.matchRequestId, record?.matchRequestId, "") || "",
    requestCategorySubcategory: pick(
      record?.requestCategorySubcategory,
      doc?.requestCategorySubcategory,
      ""
    ),
    createdAt: pick(doc?.createdAt, record?.createdAt, ""),
    userId: pick(record?.userId, record?.user?._id, doc?.userId, ""),
    patientMobile: pick(record?.patientMobile, record?.user?.mobile, ""),
    patientName: getPatientName(record),
    location: getPatientLocation(record),
    avatar: getPatientAvatar(record),
    previewImage: pick(
      doc?.previewImage,
      doc?.thumbnailUrl,
      doc?.previewUrl,
      doc?.url,
      doc?.fileUrl,
      doc?.downloadUrl,
      record?.fileUrl,
      record?.previewImage
    ),
    fileName: pick(doc?.originalName, doc?.filename, doc?.name, "Document"),
    fileSize: sizeText,
    uploadedAt: formatUploadedAt(
      pick(doc?.createdAt, doc?.uploadedAt, record?.createdAt, record?.uploadedAt)
    ),
    type,
    status: String(pick(doc?.status, record?.status, "pending")).toLowerCase(),
    rejectionReason: pick(doc?.rejectionReason, record?.rejectionReason, ""),
    url: pick(doc?.url, doc?.fileUrl, doc?.downloadUrl, record?.fileUrl, record?.url, ""),
    key: pick(doc?.key, ""),
    raw: doc,
  };
};

const normalizePrescriptionList = (payload) => {
  const records = firstArray(
    payload?.items,
    payload?.data,
    payload?.rows,
    payload?.prescriptions
  );

  const flatDocuments = [];

  records.forEach((record, recordIndex) => {
    const docs = getDocsFromRecord(record);

    if (!docs.length) {
      const hasSingleFile =
        record?.url || record?.fileUrl || record?.downloadUrl || record?.filename || record?.originalName;

      if (hasSingleFile) {
        flatDocuments.push(
          normalizeDocument(
            record,
            {
              originalName: pick(record?.originalName, record?.filename, `Document-${recordIndex + 1}`),
              type: pick(record?.type, record?.documentType, "prescription"),
              url: pick(record?.url, record?.fileUrl, record?.downloadUrl, ""),
              size: pick(record?.size, record?.fileSize, ""),
              createdAt: pick(record?.createdAt, record?.uploadedAt, ""),
            },
            0
          )
        );
      }
      return;
    }

    docs.forEach((doc, docIndex) => {
      flatDocuments.push(normalizeDocument(record, doc, docIndex));
    });
  });

  return flatDocuments;
};

const normalizePrescriptionDetail = (payload) => {
  const record =
    payload?.data?.item ||
    payload?.data?.prescription ||
    payload?.data ||
    payload?.item ||
    payload?.prescription ||
    payload;

  if (!record || typeof record !== "object") {
    return {
      patient: {
        patientName: "Patient",
        location: "",
        avatar: "",
        previewImage: "",
      },
      documents: [],
      raw: {},
    };
  }

  let docs = getDocsFromRecord(record).map((doc, index) =>
    normalizeDocument(record, doc, index)
  );

  if (!docs.length) {
    const rootDocs = firstArray(
      payload?.documents,
      payload?.files,
      payload?.prescriptions,
      payload?.reports,
      payload?.medicalReports,
      payload?.attachments,
      payload?.uploads
    );

    docs = rootDocs.map((doc, index) => normalizeDocument(record, doc, index));
  }

  if (!docs.length && (record?.url || record?.fileUrl || record?.downloadUrl)) {
    docs = [normalizeDocument(record, record, 0)];
  }

  const firstPreview = docs.find((doc) => doc.previewImage)?.previewImage || "";

  return {
    patient: {
      patientName: getPatientName(record),
      location: getPatientLocation(record),
      avatar: getPatientAvatar(record),
      previewImage: firstPreview,
    },
    documents: docs,
    raw: record,
  };
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.response?.data?.msg ||
  error?.message ||
  fallback;

export const getAdminPrescriptions = createAsyncThunk(
  "prescriptions/getAdminPrescriptions",
  async (
    { status = "all", page = 1, limit = 50, type: docType, search = "" } = {},
    { rejectWithValue }
  ) => {
    try {
      const isPending = status === "pending";

      const params = { page, limit };
      const query = String(search || "").trim();
      if (query) params.search = query;
      if (
        docType &&
        ["prescription", "report", "insurance"].includes(String(docType).toLowerCase())
      ) {
        params.type = String(docType).toLowerCase();
      }

      const res = await API.get(
        isPending ? endpoints.ADMIN_PENDING_PRESCRIPTIONS : endpoints.ADMIN_PRESCRIPTIONS,
        {
          params,
        }
      );

      const payload = res?.data || {};
      const documents = normalizePrescriptionList(payload);

      return {
        documents,
        pagination: payload?.pagination || { page, limit, total: documents.length },
        raw: payload,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to load prescriptions"));
    }
  }
);

export const getAdminPrescriptionById = createAsyncThunk(
  "prescriptions/getAdminPrescriptionById",
  async (prescriptionId, { rejectWithValue }) => {
    try {
      const res = await API.get(endpoints.adminPrescriptionById(prescriptionId));
      const payload = res?.data || {};
      return normalizePrescriptionDetail(payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to load prescription details"));
    }
  }
);

export const acceptAdminPrescription = createAsyncThunk(
  "prescriptions/acceptAdminPrescription",
  async (prescriptionId, { rejectWithValue }) => {
    try {
      const res = await API.post(endpoints.adminAcceptPrescription(prescriptionId));
      return {
        prescriptionId,
        payload: res?.data || {},
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to accept prescription"));
    }
  }
);

export const rejectAdminPrescription = createAsyncThunk(
  "prescriptions/rejectAdminPrescription",
  async ({ prescriptionId, rejectionReason }, { rejectWithValue }) => {
    try {
      const res = await API.post(endpoints.adminRejectPrescription(prescriptionId), {
        rejectionReason: rejectionReason || "Rejected by admin",
      });

      return {
        prescriptionId,
        payload: res?.data || {},
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to reject prescription"));
    }
  }
);

export const acceptMultipleAdminPrescriptions = createAsyncThunk(
  "prescriptions/acceptMultipleAdminPrescriptions",
  async (prescriptionIds, { rejectWithValue }) => {
    try {
      const res = await API.post(endpoints.ADMIN_ACCEPT_MULTIPLE_PRESCRIPTIONS, {
        prescriptionIds,
      });

      return {
        prescriptionIds,
        payload: res?.data || {},
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to accept multiple prescriptions"));
    }
  }
);

export const rejectMultipleAdminPrescriptions = createAsyncThunk(
  "prescriptions/rejectMultipleAdminPrescriptions",
  async ({ prescriptionIds, rejectionReason }, { rejectWithValue }) => {
    try {
      const res = await API.post(endpoints.ADMIN_REJECT_MULTIPLE_PRESCRIPTIONS, {
        prescriptionIds,
        rejectionReason: rejectionReason || "Rejected by admin",
      });

      return {
        prescriptionIds,
        payload: res?.data || {},
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to reject multiple prescriptions"));
    }
  }
);
